/**
 * @fileoverview `useAuthShellTranslate(t)` — the auth-shell corpus's translator.
 * @package @puffin-core/auth-shell
 *
 * Mirrors `@puffin-core/login-shell`'s `useLoginTranslate`, deliberately: the two packages sit side
 * by side on the same screens and a second, differently-shaped resolution order would be a bug
 * waiting to happen.
 *
 * ## Resolution order
 *
 *   1. the consumer's optional `t` prop — so the published component contract is unchanged for any
 *      caller that already supplies one;
 *   2. the `authShell` i18next namespace on the instance in the React tree — registered by
 *      `loadAuthShellStrings` via the `@puffin-core/i18n-namespaces` registry;
 *   3. the English bundle — the degrade path, byte-identical to this package's behaviour before it
 *      had any i18n at all.
 *
 * An app that never registers the namespace renders exactly what it rendered before: English, no
 * crash, and — critically — **no raw dotted key**, which is what `resolveString`'s key-echo check
 * buys (react-i18next returns the key itself for an unregistered namespace).
 *
 * ## No interpolation, on purpose
 *
 * Every string in this corpus is static chrome. `login-shell` needs `interpolate()` because its
 * bundles use single-curly `{email}` placeholders that i18next's `{{…}}` interpolator never touches;
 * there is nothing here to expand, so adding the machinery would be dead code. **If a placeholder is
 * ever added to `en.js`/`es.js`, port `interpolate()` across at the same time** — react-i18next's
 * no-instance `t` returns a `defaultValue` WITHOUT interpolating it, which is how page-shell once
 * shipped a literal `{{label}}` into an `aria-label`.
 *
 * ⚠️ Hook-placement rule (RECIPE §3): call this immediately above the first computed hook that
 * consumes the translator. Higher and `web/component-state-order` reports every translator-free
 * `useCallback` beneath it; lower and you ship a TDZ crash that lint scores 0 on.
 */

import { createInterpolator } from '@puffin-core/i18n-bootstrap/strings-kit';
import { createNamespaceTranslate } from '@puffin-core/i18n-bootstrap/namespace-translate';

import { AUTH_SHELL_NAMESPACE } from '../strings/namespace.js';
import { resolveString } from '../strings/resolveString.js';

/**
 * Build the auth-shell translator for a component.
 *
 * I18N-RENDER-002 Phase 0 (2026-08-20): bound from the ONE shared factory.
 *
 * ✅ **This also discharges a standing TODO in this package.** The hand-rolled hook took no `params`
 * and did no interpolation, because this bundle currently has no placeholders — and its own docblock
 * required that *"if a placeholder is ever added to `en.js`/`es.js`, port `interpolate()` across at
 * the same time"*. The factory supplies interpolation unconditionally, so that obligation is now
 * structural rather than a comment somebody has to remember. `onMissing: 'keep'` means a
 * placeholder-free bundle is passed through byte-for-byte, so today's behaviour is unchanged.
 *
 * @param {((key: string) => string|undefined|null)} [consumerT] - optional `t` prop from the caller
 * @returns {(key: string, params?: object) => string|undefined}
 */
export const useAuthShellTranslate = createNamespaceTranslate({
  namespace: AUTH_SHELL_NAMESPACE,
  resolveString,
  interpolate: createInterpolator({ braces: 'single', onMissing: 'keep' }),
});

export default useAuthShellTranslate;
